/**
 * Onboarding d'un mandataire — le barème des trois premiers mois et les deux
 * engagements de croisière. SOURCE UNIQUE (KST-2057).
 *
 * Décision : email « Bilan et arbitrage S1 » de Thomas (§ 4) et réunion de
 * direction du 05/10/2026. Lu par :
 *   - la matrice d'attribution (`matrice-attribution.ts`, plancher d'amorçage
 *     par palier, plancher de croisière) ;
 *   - les mesures d'onboarding (valorup-app, `lib/onboarding-mesures.ts`) ;
 *   - les écrans (fiche RH, Mon équipe, tableau de bord du mandataire) ;
 *   - la fiche wiki et, à terme, le gabarit du contrat de mandat (T15).
 *
 * ⚠️ Un chiffre de ce barème ne se recopie nulle part (`chiffre-unique.md`) :
 * `onboarding-recopie.test.ts` balaie valorup-app et packages. Ce fichier est
 * PUR : aucune base, aucune date courante.
 *
 * | Mois | Audits fournis par RemUp | Objectif |
 * | M1   | 0                        | 10 audits positionnés par lui-même (cold call sur ses contacts d'arrivée) |
 * | M2   | 15                       | 8 % de transformation (audit tenu → contrat signé) |
 * | M3   | 30                       | ≈ 15 % de transformation |
 *
 * Verdict à chaque fin de mois : atteint → mois suivant (après M3 : croisière) ;
 * à consolider → prolongation de 1 à 2 mois ; pas investi / pas au niveau → arrêt.
 *
 * Croisière (dès M4) : RemUp fournit jusqu'à 30 audits positionnés par mois,
 * dans la limite du budget pub — un PLANCHER, réduit au prorata quand la somme
 * des planchers dépasse `capacite.partPlanchersMax` (KST-2284) ; la matrice
 * décide au-dessus (« 30 n'est pas un max, c'est la matrice ensuite », plafond
 * `capacite.rdvMaxParMandataire`). Le contrat le dit ainsi depuis S2-2026-k
 * (`texteCroisiere('acte')`, KST-2297). En échange :
 *   - engagement 1 (dès M4) : transformation > 15 % en moyenne glissante sur
 *     3 mois ; sinon point mensuel avec le team leader, puis RDV direction ;
 *   - engagement 2 (dès M6) : 30 % des signatures en leads EXTERNES (amenés par
 *     le mandataire : `estExterne`, `lib/origine-dossier.ts`) ou au moins 2
 *     signatures externes par mois ; 3 mois consécutifs sans l'atteindre →
 *     RemUp peut arrêter.
 */

/**
 * Date d'effet du barème d'onboarding : il s'applique à tout mandataire dont la
 * date d'ARRIVÉE RH (`users.start_date`) est ce jour-là ou après. Arbitrage
 * d'Eliott du 06/10/2026 (KST-2080) : « tu peux le faire pour tous les
 * mandataires à partir du batch du 10/09 » — les neuf entrés le 10/09/2026, et
 * ceux d'après.
 *
 * ⚠️ DISTINCTE de `CANAUX_DEPUIS` (13/10/2026, `canal-constantes.ts`), avec
 * laquelle elle était confondue jusqu'à ce lot. Les deux dates ne décident pas
 * de la même chose : le barème dit QUI REÇOIT COMBIEN d'audits (planchers de la
 * matrice, points d'étape, engagements) ; les canaux disent le TARIF d'un
 * rendez-vous (1 000 € de reco ou 550 € de RDV RemUp, Annexe Y). Rendre le
 * barème rétroactif au 10/09 ne touche à aucune rémunération ; rendre les
 * canaux rétroactifs réécrirait des montants dus — c'est pourquoi l'une recule
 * et pas l'autre. Un test refuse leur égalité, pour qu'un réalignement soit une
 * décision et non un copier-coller.
 */
export const BAREME_ONBOARDING_DEPUIS = '2026-09-10';

/**
 * Date d'effet du barème POUR TOUS (KST-2284) : à partir de ce jour, le barème
 * s'applique aussi aux mandataires arrivés AVANT `BAREME_ONBOARDING_DEPUIS`
 * (« ancien régime », treize personnes au 11/10/2026), selon leur ancienneté
 * RÉELLE — le mois d'onboarding compté depuis `users.start_date`, avec la
 * bascule au 16 : M1 0, M2 15, M3 30, puis le plancher de croisière dès M4, et
 * les deux engagements. Décision d'Eliott du 11/10/2026 : « le barème, c'est
 * pour tout le monde » ; et « les 30 audits fournis viennent APRÈS le drill et
 * les mois d'onboarding ».
 *
 * SANS RÉTROACTIVITÉ, à deux niveaux :
 *   · la matrice (planchers) juge sur le JOUR du calcul : un calcul fait avant
 *     cette date garde l'ancien régime ; le mois de bascule, un ancien garde le
 *     meilleur des deux planchers (rien de défavorable rétroactivement) ;
 *   · les mesures mensuelles (engagements, points mensuels, verdicts) ne
 *     comptent, pour un ancien, aucun mois antérieur à
 *     `PREMIER_MOIS_BAREME_POUR_TOUS` — le premier mois civil ENTIER après la
 *     date d'effet. Aucune alerte « trois mois sous l'objectif » ne peut donc
 *     s'appuyer sur un mois où l'engagement ne lui était pas opposé.
 *
 * Le contrat signé d'un ancien ne dit pas cela : l'écart est régularisé par
 * l'avenant annuel (`rh-avenant-annuel-ecarts.ts`). Un test refuse l'égalité de
 * cette date avec `BAREME_ONBOARDING_DEPUIS` et `CANAUX_DEPUIS`.
 */
export const BAREME_POUR_TOUS_DEPUIS = '2026-10-12';

/** Le premier mois civil (YYYY-MM) qui commence le jour même ou après `jour` (YYYY-MM-DD). */
export function premierMoisEntierDepuis(jour: string): string {
  const an = Number(jour.slice(0, 4)), mois = Number(jour.slice(5, 7)), j = Number(jour.slice(8, 10));
  const total = an * 12 + mois - 1 + (j > 1 ? 1 : 0);
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`;
}

/**
 * Le premier mois de MESURE du barème pour un ancien (`2026-11`) : ses
 * engagements, ses points mensuels et ses verdicts ne regardent aucun mois
 * antérieur. Dérivé de la date d'effet, jamais écrit.
 */
export const PREMIER_MOIS_BAREME_POUR_TOUS = premierMoisEntierDepuis(BAREME_POUR_TOUS_DEPUIS);

const estDate = (d: string | null | undefined): d is string => /^\d{4}-\d{2}-\d{2}/.test(d ?? '');

/**
 * La date d'onboarding RETENUE d'un mandataire — celle depuis laquelle on compte
 * son M1, son M2, son M3. Point d'extension de l'arbitrage du 12/10/2026 (ONB-7,
 * ⏸ décision d'Eliott) : chaque team leader enverra à Thomas une date
 * « considérée », éventuellement recalée, pour chacun de ses mandataires encore
 * dans ses trois premiers mois.
 *
 * Aujourd'hui elle vaut, SANS RIEN CHANGER, la date d'arrivée RH
 * (`users.start_date`), et à défaut celle du dossier RH (`rh_dossier.start_date`)
 * — exactement ce que lisaient déjà les points d'étape. Le jour où la décision
 * tombe (colonne dédiée, motif, auteur), c'est CETTE fonction qui la lit, et ses
 * appelants suivent. ⚠️ Ce que la décision doit trancher avant : (a) la date
 * décale-t-elle seulement le jugement, ou aussi les planchers de la matrice ;
 * (b) mois civil + bascule au 16, ou un mois de présence ; (c) qui la saisit ;
 * (d) un ancien recalé après le 10/09 reste-t-il ancien. Elle ne touche JAMAIS
 * `enabled_since` (booking) ni la date de démarrage imprimée au contrat.
 *
 * Appelants à ce jour : les points d'étape (`rh-points-etape.ts`) et les mesures
 * servies aux écrans (`api/onboarding/mesures`). Lisent encore la colonne en
 * direct, à raccorder avec la décision : la matrice
 * (`attribution-matrice/mesures.ts`), les alertes (`onboarding-alertes.ts`), les
 * échéances des points (`rh-db.ts`, `rh_dossier.start_date`), la cooptation, les
 * cibles de performance et le cron des points mensuels (en SQL).
 */
export function dateOnboardingRetenue(dates: {
  /** `users.start_date` — la date d'arrivée RH. */
  arriveeRh?: string | null;
  /** `rh_dossier.start_date` — repli quand le compte n'a pas de date. */
  dossierRh?: string | null;
}): string | null {
  return dates.arriveeRh || dates.dossierRh || null;
}

/** Arrivé (date RH) à partir de la date d'effet du barème d'origine : il y est depuis son arrivée. */
export function arriveSousBareme(dateArrivee: string | null | undefined): boolean {
  return estDate(dateArrivee) && dateArrivee.slice(0, 10) >= BAREME_ONBOARDING_DEPUIS;
}

/** Le barème pour tous est-il en vigueur ce jour-là (YYYY-MM-DD ou ISO) ? */
export function baremePourTousLe(jour: string | null | undefined): boolean {
  return estDate(jour) && jour.slice(0, 10) >= BAREME_POUR_TOUS_DEPUIS;
}

/**
 * Le barème s'applique-t-il à ce mandataire pour le mois civil `mois` (YYYY-MM) ?
 * Arrivé sous le barème : toujours. Ancien (date connue) : à partir de
 * `PREMIER_MOIS_BAREME_POUR_TOUS`. Sans date d'arrivée : jamais — son mois
 * d'onboarding est inconnu, on ne le juge pas.
 */
export function baremeAppliqueAuMois(dateArrivee: string | null | undefined, mois: string): boolean {
  if (!estDate(dateArrivee)) return false;
  return arriveSousBareme(dateArrivee) || mois.slice(0, 7) >= PREMIER_MOIS_BAREME_POUR_TOUS;
}

/**
 * Le premier mois qu'une mesure mensuelle (fenêtre glissante, mois consécutifs)
 * a le droit de regarder : aucune borne pour un mandataire arrivé sous le
 * barème, `PREMIER_MOIS_BAREME_POUR_TOUS` pour un ancien.
 */
export function debutMesureBareme(dateArrivee: string | null | undefined): string | null {
  return arriveSousBareme(dateArrivee) ? null : PREMIER_MOIS_BAREME_POUR_TOUS;
}

/**
 * Un engagement de croisière court-il ce mois-ci ? Le barème doit s'appliquer
 * au mois (`baremeAppliqueAuMois`) ET le mandataire doit avoir atteint le mois
 * d'onboarding de l'engagement (M4 pour le 1, M6 pour le 2). LA définition, lue
 * par les mesures, les alertes, les cibles du tableau de performance.
 */
export function engagementApplicable(engagement: 1 | 2, dateArrivee: string | null | undefined, mois: string): boolean {
  if (!baremeAppliqueAuMois(dateArrivee, mois)) return false;
  const m = moisOnboarding(dateArrivee, mois);
  const depuis = engagement === 1 ? ONBOARDING_MANDATAIRE.engagement1.depuisMois : ONBOARDING_MANDATAIRE.engagement2.depuisMois;
  return m !== null && m >= depuis;
}

export type ObjectifPalier =
  | { nature: 'audits_positionnes'; minimum: number }
  | { nature: 'transformation'; minimum: number };

export interface PalierOnboarding {
  /** M1, M2, M3 — numéro du mois d'onboarding, à partir de 1. */
  mois: number;
  /** Audits publicitaires que RemUp fournit ce mois-là (plancher de la matrice). */
  auditsFournis: number;
  objectif: ObjectifPalier;
  libelle: string;
}

export const ONBOARDING_MANDATAIRE = {
  /**
   * Date d'effet du barème (= `BAREME_ONBOARDING_DEPUIS`, la seule source) : un
   * mandataire ARRIVÉ avant cette date était sur l'ancien régime (plancher
   * 15 → 30 en ligne droite sur trois mois de rotation, sans plancher de
   * croisière) jusqu'au `BAREME_POUR_TOUS_DEPUIS` (12/10/2026) ; depuis, il suit
   * le barème selon son ancienneté réelle, sans rétroactivité. Lue par la
   * matrice (`amorcage.baremeDepuis`, défaut), les mesures, les alertes, les
   * points d'étape et l'Annexe X du contrat de mandat.
   */
  enVigueurDepuis: BAREME_ONBOARDING_DEPUIS,

  paliers: [
    { mois: 1, auditsFournis: 0, objectif: { nature: 'audits_positionnes', minimum: 10 }, libelle: 'Apprentissage : 10 audits positionnés par lui-même' },
    { mois: 2, auditsFournis: 15, objectif: { nature: 'transformation', minimum: 0.08 }, libelle: '15 audits fournis, 8 % de transformation' },
    { mois: 3, auditsFournis: 30, objectif: { nature: 'transformation', minimum: 0.15 }, libelle: '30 audits fournis, 15 % de transformation' },
  ] as readonly PalierOnboarding[],

  /** Après le dernier palier : la croisière. */
  croisiere: {
    /** Premier mois de croisière (M4). */
    depuisMois: 4,
    /** Audits fournis par mois — un plancher (jamais un plafond), borné par le budget pub (KST-2284). */
    auditsFournisMin: 30,
  },

  engagement1: {
    depuisMois: 4,
    /** Transformation minimale (signés ÷ audits tenus), en moyenne glissante. */
    transformationMin: 0.15,
    fenetreMois: 3,
  },

  engagement2: {
    depuisMois: 6,
    /** Part minimale des signatures du mois venues de leads externes… */
    partSignaturesExternesMin: 0.3,
    /** …ou, à défaut, ce nombre de signatures externes dans le mois. */
    signaturesExternesMinMois: 2,
    /** Mois consécutifs sans l'atteindre avant que RemUp puisse arrêter. */
    toleranceMoisConsecutifs: 3,
  },

  /** « À consolider » : prolongation d'un ou deux mois du palier en cours. */
  prolongation: { minMois: 1, maxMois: 2 },

  /**
   * Verdict PROPOSÉ par le barème (jamais écrit : T14 pose le verdict humain).
   * « À consolider » = au moins cette fraction de l'objectif, « arrêt » en
   * dessous. Hypothèse de ce lot (05/10/2026) : la décision ne fixe pas de
   * seuil entre « à consolider » et « pas au niveau ».
   */
  seuilConsolider: 0.5,

  /**
   * Un mandataire arrivé le 16 ou après compte son M1 à partir du mois
   * suivant : trois jours de septembre ne font pas un mois d'apprentissage.
   */
  jourBasculeMois: 16,

  definitions: {
    transformation: 'contrats signés ÷ audits tenus (visio d’audit d’au moins 5 minutes, la définition commune à tous les écrans), par mois de rendez-vous',
    externe: 'lead amené par le mandataire lui-même : reco (lien, landing, cold call sur nos listes, client déjà signé) ou apporteur qu’il a amené — `estExterne`',
    fourni: 'audit publicitaire distribué par la rotation RemUp (site, publicité, apporteur de la maison)',
  },
} as const;

export type VerdictOnboarding = 'atteint' | 'a_consolider' | 'arret';

export const LIBELLES_VERDICT: Record<VerdictOnboarding, string> = {
  atteint: 'Atteint — mois suivant',
  a_consolider: 'À consolider — prolongation de 1 à 2 mois',
  arret: 'Pas au niveau — arrêt',
};

// -----------------------------------------------------------------------------
// Les points d'étape (KST-2059, T14) — ce qu'un humain POSE, et quand
// -----------------------------------------------------------------------------
//
// Un point à chaque fin de mois d'amorçage (M1, M2, M3), puis un point mensuel
// en croisière avec le team leader. Le verdict d'un palier est celui du barème
// (`VerdictOnboarding`) ; en croisière, « à consolider » n'a pas de sens — un
// mois sans engagement tenu vaut une ALERTE (point mensuel renforcé, puis RDV
// direction par `onboarding-alertes.ts`), ou l'arrêt.

/** Verdict d'un point d'étape : palier (M1 → M3) ou croisière. */
export type VerdictPointEtape = VerdictOnboarding | 'alerte';

/** Ce qu'un team leader peut poser à la fin d'un PALIER. */
export const VERDICTS_PALIER: readonly VerdictOnboarding[] = ['atteint', 'a_consolider', 'arret'];

/** Ce qu'il peut poser à un point MENSUEL de croisière. */
export const VERDICTS_CROISIERE: readonly VerdictPointEtape[] = ['atteint', 'alerte', 'arret'];

export const LIBELLES_VERDICT_POINT: Record<VerdictPointEtape, string> = {
  ...LIBELLES_VERDICT,
  alerte: 'Alerte — engagement non tenu, point renforcé le mois prochain',
};

/** Dernier jour d'un mois civil YYYY-MM, en YYYY-MM-DD. */
export function finDeMoisCivil(moisCivil: string): string {
  const an = Number(moisCivil.slice(0, 4)), mois = Number(moisCivil.slice(5, 7));
  const dernier = new Date(Date.UTC(an, mois, 0)).getUTCDate();
  return `${moisCivil}-${String(dernier).padStart(2, '0')}`;
}

/**
 * L'échéance du point d'étape M`numero` d'un mandataire arrivé le
 * `dateArrivee` : la fin du mois civil de ce palier, DÉCALÉE des mois de
 * prolongation accordés aux points précédents (« à consolider » = 1 ou 2 mois
 * de plus sur le même palier). Rend `null` sans date d'arrivée lisible.
 */
export function echeancePointEtape(dateArrivee: string | null | undefined, numero: number, prolongationMois = 0): string | null {
  const d = (dateArrivee ?? '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return null;
  return finDeMoisCivil(moisCivilOnboarding(d, numero + Math.max(0, Math.floor(prolongationMois))));
}

/** Le point mensuel de croisière du mois civil `mois` est dû le 2 du mois suivant. */
export function echeancePointMensuel(mois: string): string {
  const an = Number(mois.slice(0, 4)), m = Number(mois.slice(5, 7));
  const total = an * 12 + m; // mois suivant, base 0 → (an*12 + m-1) + 1
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}-02`;
}

/** Dernier mois d'amorçage (M3) : au-delà, c'est la croisière. */
export function dernierMoisAmorcage(): number {
  return ONBOARDING_MANDATAIRE.paliers[ONBOARDING_MANDATAIRE.paliers.length - 1].mois;
}

/** Le palier d'un mois d'onboarding, ou `null` en croisière. `mois` ≤ 1 vaut M1. */
export function palierDuMois(mois: number): PalierOnboarding | null {
  const m = Math.max(1, Math.floor(mois));
  return ONBOARDING_MANDATAIRE.paliers.find((p) => p.mois === m) ?? null;
}

/**
 * Audits fournis par RemUp au mois d'onboarding `mois` (1 = M1) : 0, 15, 30,
 * puis le plancher de croisière. C'est le PLANCHER du barème, que la matrice
 * réduit au prorata quand le budget pub ne suffit pas (`reduirePlanchers`).
 */
export function auditsFournisAuMois(mois: number): number {
  const p = palierDuMois(mois);
  return p ? p.auditsFournis : ONBOARDING_MANDATAIRE.croisiere.auditsFournisMin;
}

/** Moyenne des audits fournis sur l'amorçage (M1 → M3) : sert au flux nécessaire de l'écran Pub. */
export function auditsFournisMoyenAmorcage(): number {
  const p = ONBOARDING_MANDATAIRE.paliers;
  return p.reduce((s, x) => s + x.auditsFournis, 0) / p.length;
}

/** Volume total garanti sur l'amorçage (somme des paliers) : au-delà, le nouveau n'en est plus un. */
export function volumeGarantiAmorcage(): number {
  return ONBOARDING_MANDATAIRE.paliers.reduce((s, x) => s + x.auditsFournis, 0);
}

/**
 * Le mois d'onboarding (1 = M1) d'un mandataire arrivé le `dateArrivee`
 * (YYYY-MM-DD) au mois civil `moisRef` (YYYY-MM). Arrivé le 16 ou après, son
 * M1 est le mois suivant. Rend 0 avant le début (mois d'arrivée tardive), et
 * `null` si la date d'arrivée est illisible.
 */
export function moisOnboarding(dateArrivee: string | null | undefined, moisRef: string): number | null {
  const d = (dateArrivee ?? '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d) || !/^\d{4}-\d{2}/.test(moisRef)) return null;
  const an = Number(d.slice(0, 4)), mois = Number(d.slice(5, 7)), jour = Number(d.slice(8, 10));
  const anRef = Number(moisRef.slice(0, 4)), moisRefN = Number(moisRef.slice(5, 7));
  const premierMois = an * 12 + mois - 1 + (jour >= ONBOARDING_MANDATAIRE.jourBasculeMois ? 1 : 0);
  return Math.max(0, anRef * 12 + moisRefN - 1 - premierMois + 1);
}

/** Les mois civils YYYY-MM de M1 à `moisRef` inclus, pour une date d'arrivée. */
export function moisCivilOnboarding(dateArrivee: string, numero: number): string {
  const an = Number(dateArrivee.slice(0, 4)), mois = Number(dateArrivee.slice(5, 7)), jour = Number(dateArrivee.slice(8, 10));
  const premier = an * 12 + mois - 1 + (jour >= ONBOARDING_MANDATAIRE.jourBasculeMois ? 1 : 0) + Math.max(1, numero) - 1;
  return `${Math.floor(premier / 12)}-${String((premier % 12) + 1).padStart(2, '0')}`;
}

/** Transformation : signés ÷ tenus, `null` sans audit tenu (rien à mesurer). */
export function transformation(signes: number, tenus: number): number | null {
  return tenus > 0 ? signes / tenus : null;
}

/** L'engagement 2 est-il tenu ce mois-ci ? `null` sans signature (rien à juger). */
export function engagement2Tenu(signaturesExternes: number, signatures: number): boolean | null {
  const e = ONBOARDING_MANDATAIRE.engagement2;
  if (signaturesExternes >= e.signaturesExternesMinMois) return true;
  if (signatures <= 0) return null;
  return signaturesExternes / signatures >= e.partSignaturesExternesMin;
}

/**
 * Verdict PROPOSÉ par le barème pour un palier, sur ce que le mois a mesuré.
 * `null` quand il n'y a rien à juger (M2 sans aucun audit tenu : on ne conclut
 * pas « arrêt » sur une absence de données). Ce n'est jamais le verdict : un
 * humain le pose (T14), ici on dit ce que le barème dirait.
 */
export function verdictPropose(
  palier: PalierOnboarding,
  mesure: { auditsPositionnesParLuiMeme: number; transformation: number | null },
): VerdictOnboarding | null {
  const seuil = ONBOARDING_MANDATAIRE.seuilConsolider;
  if (palier.objectif.nature === 'audits_positionnes') {
    const r = mesure.auditsPositionnesParLuiMeme / palier.objectif.minimum;
    return r >= 1 ? 'atteint' : r >= seuil ? 'a_consolider' : 'arret';
  }
  if (mesure.transformation === null) return null;
  const r = mesure.transformation / palier.objectif.minimum;
  return r >= 1 ? 'atteint' : r >= seuil ? 'a_consolider' : 'arret';
}

/**
 * Ce que RemUp promet en croisière, en une phrase — LA formulation, lue par les
 * écrans, l'avenant annuel, le contrat de mandat (Art. 9 et Annexe X § 3
 * depuis S2-2026-k, KST-2297) et vérifiée dans le guide et les fiches
 * (KST-2284, décision d'Eliott du 11/10/2026 : « garde la performance, dans la
 * limite du budget pub »). Plus jamais « 30 garantis » : le plancher de
 * croisière est borné par la part du flux réservée aux planchers
 * (`capacite.partPlanchersMax` de la matrice), réduit au prorata au-delà.
 *
 * `registre` : `'courant'` pour les écrans et les fiches ; `'acte'` pour le
 * contrat et l'avenant — la MÊME phrase, aux deux mots près qu'un acte écrit en
 * entier (« audits fournis », « budget publicitaire ») et à l'ordinal
 * typographique des autres clauses (« 4ᵉ »). Une seule fonction : changer la
 * promesse change les écrans et l'acte d'un coup (`chiffre-unique.md`).
 */
export function texteCroisiere(
  registre: 'courant' | 'acte' = 'courant',
  // Le contrat passe SES objectifs (`objectifsContrat().croisiere`) : le
  // gabarit lit le barème par un seul chemin, et un test qui remplace le barème
  // voit l'acte suivre (`contrat-objectifs-suivent-bareme.test.ts`).
  c: { depuisMois: number; auditsFournisMin: number } = ONBOARDING_MANDATAIRE.croisiere,
): string {
  const acte = registre === 'acte';
  return `jusqu'à ${c.auditsFournisMin} audits ${acte ? 'fournis ' : ''}par mois dès le ${c.depuisMois}${acte ? 'ᵉ' : 'e'} mois, dans la limite du budget ${acte ? 'publicitaire' : 'pub'} ; au-dessus, la matrice répartit selon la performance`;
}

/** Pourcentage lisible (« 15 % »), depuis une fraction. */
export function pourcent(x: number): string {
  return `${Math.round(x * 100)} %`;
}
